'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useScheduleWriteAuthority, type ScheduleWriteOperation } from './useScheduleWriteAuthority';
import { acquirePhysicalWrite, getPhysicalWrite, releasePhysicalWrite, SCHEDULE_WRITE_SCOPE } from '../hooks/physicalWriteSlot';

export interface BlankSchedulePayload { name: string; term_id: string; is_public: true; classes: string[] }
export interface BlankScheduleCreationOptions {
  ownerId: string | null; scheduleId: string | null; routeKey: string; term: string; name: string;
  enabled: boolean; primaryScheduleId: string | null;
  isRouteCurrent: () => boolean;
  createSchedule: (payload: BlankSchedulePayload) => Promise<{ schedule_id?: string } | null>;
  assignPrimary: (scheduleId: string) => Promise<unknown>;
  navigate: (scheduleId: string) => void;
  /** Only the initial chooser may create without an existing source schedule. */
  mode?: 'toolbar' | 'chooser';
  /** Checked only before initial physical admission; never retires an admitted chain. */
  canStart?: () => boolean;
}
export interface BlankScheduleCreationReceipt { identity: object; isCurrent: () => boolean }
interface Checkpoint { id: string | null; unconfirmed: boolean; primaryAssigned: boolean; completed: boolean }

/** One blank create/setup/open chain; the chooser's active review is separate from its same-intent checkpoint. */
export function useBlankScheduleCreation(options: BlankScheduleCreationOptions) {
  const { ownerId, scheduleId, routeKey, term, name, enabled } = options;
  const mode = options.mode ?? 'toolbar';
  const intent = useMemo(() => ({ mode, ownerId, scheduleId, routeKey, term, name }),
    [mode, ownerId, scheduleId, routeKey, term, name]);
  const context = useMemo(() => ({ intent, enabled }), [intent, enabled]);
  // Availability/view changes retire active continuations, not a same-intent create outcome.
  const checkpointIdentity = intent;
  const authority = useScheduleWriteAuthority({ ownerId: enabled ? ownerId : null,
    scheduleId, routeKey, term, draft: name });
  const committed = useRef<{ context: typeof context; options: BlankScheduleCreationOptions } | null>(null);
  const checkpoint = useRef<Checkpoint | null>(null);
  const activeToolbarOperation = useRef<ScheduleWriteOperation | null>(null);
  const getActiveToolbarOperation = useCallback(() => activeToolbarOperation.current, []);
  const retired = useRef<typeof context | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  useLayoutEffect(() => { committed.current = { context, options }; });
  useLayoutEffect(() => { checkpoint.current = null; }, [checkpointIdentity]);
  useLayoutEffect(() => {
    retired.current = null; setError(null); setStatus(null);
    return () => { if (committed.current?.context === context) committed.current = null; };
  }, [context]);

  const performCreation = useCallback(async (review?: BlankScheduleCreationReceipt): Promise<boolean> => {
    const current = committed.current;
    if (current?.context !== context || retired.current === context || !enabled || !current.options.isRouteCurrent()
      || (mode === 'chooser' && (!review || !review.isCurrent()))) return false;
    if (mode === 'chooser' && !ownerId) throw new Error('Sign in to create a schedule.');
    if (current.options.canStart?.() === false) return false;
    // Toolbar writers retain their genuine loaded-SID authority. No sentinel or
    // general authority relaxation is needed for the chooser's initial null SID.
    let operation: { isCurrent: () => boolean; finish: () => void } | null;
    if (mode === 'toolbar') {
      const owned = authority.begin();
      operation = owned;
      if (owned) activeToolbarOperation.current = owned;
    }
    else {
      const token = Symbol('chooser-blank-create');
      operation = acquirePhysicalWrite(SCHEDULE_WRITE_SCOPE, token)
        ? { isCurrent: () => getPhysicalWrite(SCHEDULE_WRITE_SCOPE) === token,
          finish: () => releasePhysicalWrite(SCHEDULE_WRITE_SCOPE, token) } : null;
    }
    if (!operation) return false;
    const activeOperation = operation;
    let saved = checkpoint.current;
    if (!saved) { saved = { id: null, unconfirmed: false, primaryAssigned: false, completed: false }; checkpoint.current = saved; }
    const isCurrent = () => activeOperation.isCurrent() && committed.current?.context === context
      && retired.current !== context && checkpoint.current === saved && committed.current.options.isRouteCurrent()
      && (mode !== 'chooser' || review!.isCurrent());
    try {
      if (!isCurrent()) return false;
      if (saved.completed) return true;
      if (saved.unconfirmed) {
        if (mode === 'toolbar') {
          setError('Creation could not be confirmed. Check your schedules before creating another.');
          return false;
        }
        throw new Error('Creation could not be confirmed.');
      }
      setError(null); setStatus(null);
      if (!saved.id) {
        saved.unconfirmed = true;
        const created = await current.options.createSchedule({ name, term_id: term, is_public: true, classes: [] });
        if (!created || typeof created.schedule_id !== 'string' || !created.schedule_id.trim()) {
          throw new Error('Creation could not be confirmed.');
        }
        saved.id = created.schedule_id; saved.unconfirmed = false;
      }
      if (!isCurrent()) return false;
      // Re-read the current profile before the dependent write; a primary may have arrived while create was pending.
      if (!saved.primaryAssigned && !committed.current!.options.primaryScheduleId) {
        await committed.current!.options.assignPrimary(saved.id);
        if (!isCurrent()) return false;
        saved.primaryAssigned = true;
      }
      if (!isCurrent()) return false;
      committed.current!.options.navigate(saved.id);
      saved.completed = true;
      if (isCurrent()) setStatus('Schedule created. Opening it now.');
      return true;
    } catch {
      if (!isCurrent()) return false;
      const message = saved.unconfirmed
        ? 'Creation could not be confirmed. Check your schedules before creating another.'
        : 'Your schedule was created, but setup could not finish. Retry to open the same schedule.';
      if (mode === 'chooser') throw new Error(message);
      setError(message);
      return false;
    } finally {
      // Keep the shared physical slot until the dispatched chain settles, including after owner replacement or unmount.
      if (activeToolbarOperation.current === activeOperation) activeToolbarOperation.current = null;
      activeOperation.finish();
    }
  }, [context, enabled, mode, ownerId, name, term, authority.begin]);

  const createNewSchedule = useCallback(async (): Promise<void> => {
    if (mode === 'toolbar') await performCreation();
  }, [mode, performCreation]);
  const createFromChooser = useCallback((review: BlankScheduleCreationReceipt): Promise<boolean> =>
    mode === 'chooser' ? performCreation(review) : Promise.resolve(false), [mode, performCreation]);
  const retireChooser = useCallback(() => {
    if (mode === 'chooser' && committed.current?.context === context) retired.current = context;
  }, [mode, context]);

  return { createNewSchedule, createFromChooser, retireChooser, getActiveToolbarOperation, isPending: authority.isPending, error, status };
}
